// role-based-dashboards — Phase 7.2: cross-org audit feed widget for the
// super_admin platform overview.
//
// Reuses the EXISTING `AuditLogTable` presentational component (already
// generic over `AuditLogEntryResponse[]`) and the `useCursorPagination` hook
// verbatim — no re-implementation of the list/expand/"load more" UI, per
// spec "deep-link to existing detail pages rather than duplicating them"
// (here: reuse existing primitives rather than duplicate them). "Load more"
// is a user-triggered cursor fetch, not a poll — compatible with the
// "Snapshot-Only Super Admin Overview" requirement (no auto-refresh).

import { listPlatformAuditLog } from '@jq4race/api-client';
import { translateApiError, useTranslation } from '@jq4race/i18n';
import type { AuditLogEntryResponse } from '@jq4race/shared';
import { TriangleAlert } from 'lucide-react';
import { useMemo } from 'react';

import { useCursorPagination } from '../../../../shared/hooks/useCursorPagination';
import { AuditLogTable } from '../../../audit-log/ui/components/AuditLogTable';

const PAGE_LIMIT = 10;

export function PlatformAuditFeed() {
  const { t } = useTranslation('super-admin-overview');
  const fetchPage = useMemo(
    () => (cursor: string | null) =>
      listPlatformAuditLog({ cursor: cursor ?? undefined, limit: PAGE_LIMIT }),
    [],
  );

  const { items, hasMore, isLoading, isFetchingNextPage, fetchNextPage, error } =
    useCursorPagination<AuditLogEntryResponse>({
      queryKey: ['super-admin-overview', 'platform-audit-log'],
      fetchPage,
    });

  return (
    <section>
      <p className="jq-eyebrow mb-4">{t('auditFeed.eyebrow')}</p>
      {error && (
        <div
          role="alert"
          className="mb-4 flex items-start gap-3 rounded-sm border border-jq-danger/40 bg-jq-danger/10 px-4 py-3 text-[13px] text-jq-danger"
        >
          <TriangleAlert className="mt-0.5 h-4 w-4 shrink-0" strokeWidth={1.75} />
          <p className="flex-1 leading-snug">{translateApiError(error, t)}</p>
        </div>
      )}
      <AuditLogTable
        items={items}
        isLoading={isLoading}
        hasMore={hasMore}
        isFetchingNextPage={isFetchingNextPage}
        onLoadMore={fetchNextPage}
      />
    </section>
  );
}
